import { createBrowserRouter, Navigate, type RouterProviderProps } from "react-router-dom";
import { AdminLayout } from "@/layouts/AdminLayout";
import { GuestRoute, ProtectedRoute, RoleGuard } from "@/app/router/ProtectedRoute";
import Login from "@/pages/Login/Login";
import ForgotPassword from "@/pages/Login/ForgotPassword";
import Dashboard from "@/pages/Dashboard/Dashboard";
import LiveRides from "@/pages/LiveRides/LiveRides";
import ScheduledRides from "@/pages/ScheduledRides/ScheduledRides";
import Rides from "@/pages/Rides/Rides";
import Drivers from "@/pages/Drivers/Drivers";
import DriverApprovals from "@/pages/DriverApprovals/DriverApprovals";
import Users from "@/pages/Users/Users";
import Rankings from "@/pages/Rankings/Rankings";
import Commission from "@/pages/Commission/Commission";
import Fares from "@/pages/Fares/Fares";
import Zones from "@/pages/Zones/Zones";
import Promos from "@/pages/Promos/Promos";
import Support from "@/pages/Support/Support";
import Reports from "@/pages/Reports/Reports";
import Settings from "@/pages/Settings/Settings";
import TeamAdmins from "@/pages/Team/Admins";
import TeamRoles from "@/pages/Team/Roles";
import NotFound from "@/pages/NotFound/NotFound";
import Forbidden from "@/pages/Forbidden/Forbidden";

export const router: RouterProviderProps["router"] = createBrowserRouter([
  {
    path: "/login",
    element: (
      <GuestRoute>
        <Login />
      </GuestRoute>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <GuestRoute>
        <ForgotPassword />
      </GuestRoute>
    ),
  },
  { path: "/403", element: <Forbidden /> },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      {
        path: "dashboard",
        element: (
          <RoleGuard path="/dashboard">
            <Dashboard />
          </RoleGuard>
        ),
      },
      {
        path: "scheduled-rides",
        element: (
          <RoleGuard path="/scheduled-rides">
            <ScheduledRides />
          </RoleGuard>
        ),
      },
      {
        path: "live-rides",
        element: (
          <RoleGuard path="/live-rides">
            <LiveRides />
          </RoleGuard>
        ),
      },
      {
        path: "rides",
        element: (
          <RoleGuard path="/rides">
            <Rides />
          </RoleGuard>
        ),
      },
      {
        path: "drivers",
        element: (
          <RoleGuard path="/drivers">
            <Drivers />
          </RoleGuard>
        ),
      },
      {
        path: "driver-approvals",
        element: (
          <RoleGuard path="/driver-approvals">
            <DriverApprovals />
          </RoleGuard>
        ),
      },
      {
        path: "users",
        element: (
          <RoleGuard path="/users">
            <Users />
          </RoleGuard>
        ),
      },
      {
        path: "rankings",
        element: (
          <RoleGuard path="/rankings">
            <Rankings />
          </RoleGuard>
        ),
      },
      {
        path: "commission",
        element: (
          <RoleGuard path="/commission">
            <Commission />
          </RoleGuard>
        ),
      },
      {
        path: "fares",
        element: (
          <RoleGuard path="/fares">
            <Fares />
          </RoleGuard>
        ),
      },
      {
        path: "zones",
        element: (
          <RoleGuard path="/zones">
            <Zones />
          </RoleGuard>
        ),
      },
      {
        path: "promos",
        element: (
          <RoleGuard path="/promos">
            <Promos />
          </RoleGuard>
        ),
      },
      {
        path: "support",
        element: (
          <RoleGuard path="/support">
            <Support />
          </RoleGuard>
        ),
      },
      {
        path: "reports",
        element: (
          <RoleGuard path="/reports">
            <Reports />
          </RoleGuard>
        ),
      },
      {
        path: "settings",
        element: (
          <RoleGuard path="/settings">
            <Settings />
          </RoleGuard>
        ),
      },
      {
        path: "team",
        children: [
          { index: true, element: <Navigate to="/team/admins" replace /> },
          {
            path: "admins",
            element: (
              <RoleGuard path="/team/admins">
                <TeamAdmins />
              </RoleGuard>
            ),
          },
          {
            path: "roles",
            element: (
              <RoleGuard path="/team/roles">
                <TeamRoles />
              </RoleGuard>
            ),
          },
        ],
      },
    ],
  },
  { path: "*", element: <NotFound /> },
]);
